import React, { useEffect, useState } from 'react';
import { AlertCircle, CalendarClock, CheckCircle2, CircleDashed, LifeBuoy, ListTree } from 'lucide-react';
import api from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { C, KpiCard } from '@/design-system/dna';
import { HelpTip } from './Help';

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

export const KPI_FILTERS = {
  avaliados: (s) => s.status !== 'nao_avaliado',
  nao_avaliado: (s) => s.status === 'nao_avaliado',
  dificuldade: (s) => s.dificuldades > 0,
  reforco: (s) => s.reforco > 0,
  reaval: (s) => s.reavaliacao_pendente,
};

function Geral({ period, year, onPick }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get('/dna-mapa/resumo', { params: { period, year } }).then(({ data }) => setR(data)).catch(() => setR(null)); }, [period, year]);
  if (!r || r.pares.length < 2) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-[12px] font-semibold hover:bg-[#FBF9FD]" style={{ borderColor: C.line, color: C.ink }} data-testid="dna-resumo-geral">
          <ListTree className="h-3.5 w-3.5" style={{ color: C.purple }} /> Todas as suas turmas: {r.avaliados}/{r.total} ({pct(r.avaliados, r.total)}%)
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-[360px] w-[min(420px,92vw)] overflow-y-auto p-2" data-testid="dna-resumo-geral-lista">
        {r.pares.map((p) => (
          <button key={`${p.class_id}-${p.subject_id}`} type="button" onClick={() => onPick(p.class_id, p.subject_id)} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-[12.5px] hover:bg-[#F5EEFA]" data-testid={`dna-resumo-par-${p.class_id}-${p.subject_id}`}>
            <span className="min-w-0 flex-1 truncate"><b>{p.class_name}</b> · {p.subject_name}</span>
            <span className="h-1.5 w-20 overflow-hidden rounded-full bg-[#EDE4F3]"><span className="block h-full rounded-full" style={{ width: `${pct(p.avaliados, p.total)}%`, background: C.green }} /></span>
            <span className="w-14 text-right tabular-nums" style={{ color: C.muted }}>{p.avaliados}/{p.total}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** Indicadores da turma/disciplina + progresso de preenchimento. Clique num indicador para filtrar a lista. */
export function Resumo({ data, kf, setKf, period, year, onPick }) {
  const st = data?.students || [];
  const n = st.length;
  const c = Object.fromEntries(Object.entries(KPI_FILTERS).map(([k, f]) => [k, st.filter(f).length]));
  const cards = [
    ['avaliados', 'Avaliados', CheckCircle2, C.green, 'Ficha concluída neste período'],
    ['nao_avaliado', 'Não avaliados', CircleDashed, '#A79BB5', 'Ainda sem registro concluído'],
    ['dificuldade', 'Com dificuldades', AlertCircle, '#F59E0B', 'Ao menos uma dificuldade registrada'],
    ['reforco', 'Com reforço indicado', LifeBuoy, '#D6336C', 'Precisam de intervenção dirigida'],
    ['reaval', 'Reavaliação pendente', CalendarClock, C.purple, 'Plano com data de reavaliação vencida'],
  ];
  return (
    <section className="space-y-3" data-testid="dna-resumo">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {cards.map(([k, l, I, col, hint], i) => (
          <div key={k} data-tour={k === 'reaval' ? 'kpi-reaval' : undefined} className="rounded-[22px]" style={kf === k ? { boxShadow: `0 0 0 2px ${col}` } : undefined}>
            <KpiCard id={`dna-${k}`} label={l} value={data ? c[k] : '—'} hint={kf === k ? 'Filtrando a lista · clique para limpar' : hint} icon={I} color={col} index={i} onClick={() => setKf(kf === k ? null : k)} />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border bg-white px-4 py-3" style={{ borderColor: C.line }} data-testid="dna-progresso-turma">
        <span className="text-[12.5px] font-bold" style={{ color: C.ink }}>Fichas concluídas<HelpTip k="situacao" /></span>
        <span className="h-2 min-w-[120px] flex-1 overflow-hidden rounded-full bg-[#EDE4F3]"><span className="block h-full rounded-full transition-[width] duration-500" style={{ width: `${pct(c.avaliados, n)}%`, background: `linear-gradient(90deg, ${C.purple}, ${C.green})` }} /></span>
        <span className="text-[12.5px] font-bold tabular-nums" style={{ color: C.ink }} data-testid="dna-progresso-valor">{c.avaliados || 0} de {n} · {pct(c.avaliados, n)}%</span>
        <Geral period={period} year={year} onPick={onPick} />
      </div>
    </section>
  );
}
